// Header.jsx
// Top bar — symbol selector, play controls,
// global EMA toggles (single chart view only)

import React, { useEffect, useRef } from 'react';
import useStore from '../store';
import { clearApiToken } from '../api';

const EMA_PERIODS = ['ema_5', 'ema_9', 'ema_20', 'ema_50', 'ema_200'];

const EMA_SOURCE_OPTIONS = ['close', 'high', 'low'];

const EMA_COLORS = {
  ema_5:   '#2962ff',
  ema_9:   '#ff6d00',
  ema_20:  '#2e7d32',
  ema_50:  '#6a1b9a',
  ema_200: '#b71c1c',
};


function Header() {
  const {
    selectedSymbol, setSelectedSymbol,
    watchlist,
    layout,
    activeView,
    chartSettings,
    setChartSetting,
    toggleChartEMA,
    playMode,     setPlayMode,
    playInterval, setPlayInterval,
  } = useStore();

  // Single chart uses slot 0 for global EMA settings
  const globalSettings = chartSettings[0];
  const timerRef       = useRef(null);
  const clearToken = useStore((s) => s.clearToken);


  // ============================================================
  // PLAY MODE — auto-cycle through watchlist
  // ============================================================
  useEffect(() => {
    if (!playMode) {
      clearTimeout(timerRef.current);
      return;
    }

    const advance = () => {
      const current = useStore.getState().selectedSymbol;
      const list    = useStore.getState().watchlist;
      const idx     = list.indexOf(current);

      if (idx >= 0 && idx < list.length - 1) {
        useStore.getState().setSelectedSymbol(list[idx + 1]);
      } else {
        // Reached end — stop play
        useStore.getState().setPlayMode(false);
        return;
      }
    };

    timerRef.current = setTimeout(() => {
      advance();
    }, playInterval * 1000);

    return () => clearTimeout(timerRef.current);
  }, [playMode, selectedSymbol, playInterval]);

  // ============================================================
  // PLAY CONTROLS
  // ============================================================
  const currentIdx = watchlist.indexOf(selectedSymbol);
  const isFirst    = currentIdx <= 0;
  const isLast     = currentIdx >= watchlist.length - 1;

  const handlePrev = () => {
    if (currentIdx > 0) setSelectedSymbol(watchlist[currentIdx - 1]);
  };

  const handleNext = () => {
    if (currentIdx < watchlist.length - 1)
      setSelectedSymbol(watchlist[currentIdx + 1]);
  };

  const handlePlay  = () => setPlayMode(true);
  const handlePause = () => setPlayMode(false);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={styles.header}>

      {/* ── PLAY CONTROLS + EMA CONTROLS — only on charts view ── */}
      {activeView === 'charts' && watchlist.length > 1 && (
        <div style={styles.section}>
          <button
            onClick={handlePrev}
            disabled={isFirst}
            style={isFirst ? styles.btnDisabled : styles.btn}
            title="Previous stock"
          >
            ←
          </button>

          {!playMode ? (
            <button
              onClick={handlePlay}
              disabled={isLast}
              style={isLast ? styles.btnDisabled : styles.btnPlay}
              title="Auto-play through watchlist"
            >
              ▶
            </button>
          ) : (
            <button
              onClick={handlePause}
              style={styles.btnPause}
              title="Pause"
            >
              ⏸
            </button>
          )}

          <button
            onClick={handleNext}
            disabled={isLast}
            style={isLast ? styles.btnDisabled : styles.btn}
            title="Next stock"
          >
            →
          </button>

          {/* Interval input */}
          <input
            type="number"
            min={1}
            max={60}
            value={playInterval}
            onChange={(e) =>
              setPlayInterval(Math.max(1, parseInt(e.target.value) || 5))
            }
            disabled={playMode}
            style={styles.intervalInput}
            title="Seconds per stock"
          />
          <span style={styles.label}>sec</span>

          {/* Stock counter */}
          <span style={styles.counter}>
            {currentIdx + 1}/{watchlist.length}
          </span>
        </div>
      )}

      {/* ── GLOBAL EMA CONTROLS — single chart view only ── */}
      {activeView === 'charts' && layout === 'single' && (
        <>
          <div style={styles.divider} />

          {/* EMA period toggles */}
          <div style={styles.section}>
            <span style={styles.label}>EMA:</span>
            {EMA_PERIODS.map((ema) => (
              <button
                key={ema}
                onClick={() => toggleChartEMA(0, ema)}
                style={{
                  ...(globalSettings.visibleEMAs[ema]
                    ? { ...styles.btn, backgroundColor: EMA_COLORS[ema],
                        color: '#fff', borderColor: EMA_COLORS[ema] }
                    : styles.btn),
                }}
                title={`Toggle ${ema.replace('ema_', 'EMA ')}`}
              >
                {ema.replace('ema_', '')}
              </button>
              
            ))}
          </div>

          {/* EMA source selector */}
          <div style={styles.section}>
            <span style={styles.label}>Source:</span>
            {EMA_SOURCE_OPTIONS.map((src) => (
              <button
                key={src}
                onClick={() => setChartSetting(0, 'emaSource', src)}
                style={
                  globalSettings.emaSource === src
                    ? styles.btnActive
                    : styles.btn
                }
              >
                {src.charAt(0).toUpperCase() + src.slice(1)}
              </button>
              
            ))}
          </div>

        </>
      )}

      {/* ── LOGOUT ── */}
      <button
        onClick={() => { clearApiToken(); clearToken(); }}
        style={{
          padding:      '5px 12px',
          background:   'transparent',
          border:       '1px solid #ddd',
          borderRadius: '4px',
          cursor:       'pointer',
          fontSize:     '12px',
          color:        '#555',
          marginRight:  '12px',
        }}
      >
        Logout
      </button>

      {/* ── APP TITLE ── */}
      <div style={styles.title}>Trading Dashboard</div>

    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  header: {
    display:         'flex',
    alignItems:      'center',
    gap:             '12px',
    padding:         '6px 16px',
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
    flexWrap:        'wrap',
    boxShadow:       '0 1px 3px rgba(0,0,0,0.08)',
    flexShrink:      0,
  },
  section: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
  },
  symbolList: {
    display:  'flex',
    flexWrap: 'wrap',
    gap:      '3px',
  },
  label: {
    color:     '#666',
    fontSize:  '11px',
    marginRight: 2,
  },
  counter: {
    fontSize:  '11px',
    color:     '#888',
    marginLeft: 4,
  },
  btn: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '16px',
  },
  btnActive: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#2962ff',
    color:           '#ffffff',
    border:          '1px solid #2962ff',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '16px',
  },
  btnPlay: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#2e7d32',
    color:           '#ffffff',
    border:          '1px solid #2e7d32',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '16px',
  },
  btnPause: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#e65100',
    color:           '#ffffff',
    border:          '1px solid #e65100',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '16px',
  },
  btnDisabled: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#bbb',
    border:          '1px solid #e0e0e0',
    borderRadius:    '3px',
    cursor:          'not-allowed',
    lineHeight:      '16px',
  },
  intervalInput: {
    width:       '36px',
    padding:     '2px 4px',
    fontSize:    '11px',
    border:      '1px solid #ddd',
    borderRadius:'3px',
    textAlign:   'center',
    color:       '#444',
  },
  divider: {
    width:           '1px',
    height:          '20px',
    backgroundColor: '#e0e0e0',
    flexShrink:      0,
  },
  title: {
    marginLeft: 'auto',
    color:      '#131722',
    fontWeight: 'bold',
    fontSize:   '15px',
  },
};

export default Header;